import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND, waLink } from "@/lib/oneyork";

const NAV = [
  { label: "Home", href: "#top" },
  { label: "Ceramic Coating", href: "#ceramic" },
  { label: "Detailing", href: "#detailing" },
  { label: "Add-ons", href: "#addons" },
  { label: "Gallery", href: "#gallery" },
  { label: "Reviews", href: "#reviews" },
  { label: "Story", href: "#about" },
  { label: "FAQs", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 transition-all duration-300"
    >
      {/* Page backdrop — blurs + darkens content behind menu */}
      <AnimatePresence>
        {open && (
          <motion.button
            type="button"
            aria-label="Close menu"
            data-testid="mobile-menu-backdrop"
            onClick={() => setOpen(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="lg:hidden fixed inset-0 bg-black/55 cursor-default"
            style={{
              backdropFilter: "blur(14px) saturate(140%)",
              WebkitBackdropFilter: "blur(14px) saturate(140%)",
            }}
          />
        )}
      </AnimatePresence>

      <div
        className={`relative mx-2 sm:mx-3 lg:mx-0 mt-2 sm:mt-3 lg:mt-0 rounded-2xl lg:rounded-none lg:rounded-b-[28px] overflow-hidden transition-all duration-300 ${
          scrolled ? "shadow-[0_12px_40px_-12px_rgba(0,0,0,0.55)]" : ""
        }`}
        style={{
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.03) 45%, rgba(255,255,255,0.02) 60%, rgba(255,255,255,0.09) 100%)",
          backdropFilter: "blur(28px) saturate(180%)",
          WebkitBackdropFilter: "blur(28px) saturate(180%)",
          border: "1px solid rgba(255,255,255,0.16)",
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -1px 0 rgba(255,255,255,0.05)",
        }}
      >
        {/* Top rim glare */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-8 top-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.85) 50%, transparent 100%)",
          }}
        />
        {/* Diagonal sheen */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "linear-gradient(120deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.06) 14%, rgba(255,255,255,0) 30%, rgba(255,255,255,0) 75%, rgba(255,255,255,0.10) 100%)",
          }}
        />

        <div className="relative pl-3 sm:pl-6 lg:pl-8 xl:pl-16 2xl:pl-28 pr-3 sm:pr-6 lg:pr-6 xl:pr-10 h-[68px] sm:h-20 lg:h-24 flex items-center justify-between gap-3 lg:gap-4">
        <a
          data-testid="header-logo"
          href="#top"
          className="flex items-center gap-3 sm:gap-4 shrink-0 h-full py-2"
        >
          <img
            src="/images/logo.png"
            alt="ONE YORK"
            className="h-full w-auto object-contain"
          />
          <span className="flex flex-col leading-tight border-l border-white/15 pl-3 sm:pl-4">
            <span className="font-display font-bold text-[14px] sm:text-base tracking-tight text-white">
              Ceramic Coating &amp; Detailing Services
            </span>
            <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.22em] text-[#d4af37] mt-0.5">
              York Region / GTA
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-4 xl:gap-6 2xl:gap-8">
          {NAV.map((n) => (
            <a
              key={n.href}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              href={n.href}
              className="text-[12px] xl:text-[13px] 2xl:text-sm text-white/70 hover:text-white transition-colors whitespace-nowrap"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            data-testid="header-quote-cta"
            href={waLink(`Hi ${BRAND.name} — I'd like a quote.`)}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#d4af37] hover:bg-[#f2c94c] text-black font-semibold text-[12px] xl:text-sm px-4 xl:px-5 py-2 xl:py-2.5 rounded-full transition-colors whitespace-nowrap"
          >
            Get a Quote
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 text-white"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

        {/* Mobile menu — expands inside the same glass banner */}
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="mobile-menu"
              data-testid="mobile-menu"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{
                height: { duration: 0.4, ease: [0.32, 0.72, 0, 1] },
                opacity: { duration: 0.25, ease: "easeOut" },
              }}
              className="lg:hidden relative overflow-hidden border-t border-white/15"
            >
              <div className="px-5 pt-5 pb-6 flex flex-col gap-1">
                {NAV.map((n, idx) => (
                  <motion.a
                    key={n.href}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                    href={n.href}
                    onClick={() => setOpen(false)}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.08 + idx * 0.03 }}
                    className="text-[15px] text-white/85 hover:text-[#d4af37] active:text-[#d4af37] transition-colors py-2.5 border-b border-white/8 last:border-b-0"
                  >
                    {n.label}
                  </motion.a>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
